import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import Select from "react-select";
import './GestioneMagazzino.css';
import Lf23Loader from "../components/Lf23Loader";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

const GestioneMagazzino = ({ isWarehouseMode = false }) => {
    const navigate = useNavigate();

    // Data
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);

    // UI state
    const [showInfo, setShowInfo] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [toast, setToast] = useState(null);

    // Pagination
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const totalPages = Math.ceil(total / limit);

    // Sorting
    const [sortKey, setSortKey] = useState("product_name");
    const [sortDir, setSortDir] = useState("asc");

    // Filters
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [selectedWarehouses, setSelectedWarehouses] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [productSearch, setProductSearch] = useState("");

    // Applied filters (solo dopo click "Applica")
    const [appliedFilters, setAppliedFilters] = useState({
        location: "",
        warehouse: "",
        category: "",
        product: "",
        productIds: ""
    });

    // Filter options (datalake + stock data)
    const [filterOptions, setFilterOptions] = useState({
        locations: [],
        products: [],
        warehouses: [],
        categories: []
    });

    // Edit modal
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [newQuantity, setNewQuantity] = useState("");
    const [saving, setSaving] = useState(false);

    // Add modal
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [addProduct, setAddProduct] = useState(null);
    const [addLocation, setAddLocation] = useState(null);
    const [addQuantity, setAddQuantity] = useState("");
    const [addSaving, setAddSaving] = useState(false);

    // Stats
    const [stats, setStats] = useState({
        totalProducts: 0,
        totalWarehouses: 0,
        totalLocations: 0
    });

    const showToast = useCallback((type, message) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 4000);
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/odoo/stock-data`, {
                params: {
                    page,
                    limit,
                    location: appliedFilters.location,
                    warehouse: appliedFilters.warehouse,
                    category: appliedFilters.category,
                    product: appliedFilters.product,
                    productIds: appliedFilters.productIds
                }
            });
            setData(res.data.data || []);
            setTotal(res.data.total || 0);

            // Aggiorna opzioni filtro warehouse/categoria dai dati stock
            if (res.data.filters) {
                setFilterOptions(prev => ({
                    ...prev,
                    warehouses: res.data.filters.warehouses || [],
                    categories: res.data.filters.categories || []
                }));
            }

            // Compute stats
            setStats({
                totalProducts: res.data.total || 0,
                totalWarehouses: res.data.totalWarehouses || 0,
                totalLocations: filterOptions.locations.length
            });
        } catch (err) {
            console.error("Errore nel caricamento dati magazzino:", err);
            showToast("error", "Errore nel caricamento dei dati");
        } finally {
            setLoading(false);
        }
    }, [page, limit, appliedFilters, filterOptions.locations.length, showToast]);

    // Carica opzioni filtro dal datalake al mount
    useEffect(() => {
        axios.get(`${API_URL}/odoo/filter-options`)
            .then(res => {
                setFilterOptions(prev => ({
                    ...prev,
                    locations: res.data.locations || [],
                    products: res.data.products || []
                }));
            })
            .catch(err => console.error("Errore caricamento opzioni filtro:", err));
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Sort handler
    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    };

    // Client-side sort on current page data
    const sortedData = [...data].sort((a, b) => {
        let aVal = a[sortKey];
        let bVal = b[sortKey];

        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();
        if (aVal == null) aVal = '';
        if (bVal == null) bVal = '';

        if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
        return 0;
    });

    // Apply filters
    const handleApplyFilters = () => {
        setAppliedFilters({
            location: selectedLocations.map(l => l.value).join(','),
            warehouse: selectedWarehouses.map(w => w.value).join(','),
            category: selectedCategories.map(c => c.value).join(','),
            product: productSearch,
            productIds: selectedProducts.map(p => p.value).join(',')
        });
        setPage(1);
    };

    const handleResetFilters = () => {
        setSelectedLocations([]);
        setSelectedWarehouses([]);
        setSelectedCategories([]);
        setSelectedProducts([]);
        setProductSearch("");
        setAppliedFilters({ location: "", warehouse: "", category: "", product: "", productIds: "" });
        setPage(1);
    };

    // Edit handlers
    const handleEditClick = (item) => {
        setEditingItem(item);
        setNewQuantity(String(item.quantity));
        setEditModalOpen(true);
    };

    const handleCancelEdit = () => {
        setEditModalOpen(false);
        setEditingItem(null);
        setNewQuantity("");
    };

    const handleConfirmEdit = async () => {
        if (!editingItem || newQuantity === "") return;

        const qty = parseFloat(newQuantity);
        if (isNaN(qty)) {
            showToast("error", "Inserire un valore numerico valido");
            return;
        }

        setSaving(true);
        try {
            const res = await axios.post(`${API_URL}/odoo/adjust-inventory`, {
                productId: editingItem.product_id,
                locationId: editingItem.location_id,
                newQuantity: qty
            });

            if (res.data.success) {
                showToast("success", res.data.message || "Quantita aggiornata con successo");
                handleCancelEdit();
                fetchData();
            } else {
                showToast("error", res.data.message || "Errore nell'aggiornamento");
            }
        } catch (err) {
            console.error("Errore nella modifica:", err);
            const errorMsg = err.response?.data?.details || err.response?.data?.error || "Errore nella modifica della quantita";
            showToast("error", errorMsg);
        } finally {
            setSaving(false);
        }
    };

    // Add handlers
    const handleCancelAdd = () => {
        setAddModalOpen(false);
        setAddProduct(null);
        setAddLocation(null);
        setAddQuantity("");
    };

    const handleConfirmAdd = async () => {
        if (!addProduct || !addLocation || addQuantity === "") return;

        const qty = parseFloat(addQuantity);
        if (isNaN(qty)) {
            showToast("error", "Inserire un valore numerico valido");
            return;
        }

        setAddSaving(true);
        try {
            const res = await axios.post(`${API_URL}/odoo/add-inventory`, {
                productId: addProduct.value,
                locationId: addLocation.value,
                newQuantity: qty
            });

            if (res.data.success) {
                showToast("success", res.data.message || "Prodotto inserito con successo");
                handleCancelAdd();
                fetchData();
            } else {
                showToast("error", res.data.message || "Errore nell'inserimento");
            }
        } catch (err) {
            console.error("Errore nell'inserimento:", err);
            const errorMsg = err.response?.data?.details || err.response?.data?.error || "Errore nell'inserimento del prodotto";
            showToast("error", errorMsg);
        } finally {
            setAddSaving(false);
        }
    };

    // Confirm handler (segna come gestito senza modificare quantità)
    const handleConfirmQuantity = async (item) => {
        try {
            const res = await axios.post(`${API_URL}/odoo/confirm-inventory`, {
                productId: item.product_id,
                locationId: item.location_id
            });
            if (res.data.success) {
                showToast("success", "Quantita confermata");
                fetchData();
            } else {
                showToast("error", res.data.message || "Errore nella conferma");
            }
        } catch (err) {
            console.error("Errore nella conferma:", err);
            showToast("error", err.response?.data?.details || "Errore nella conferma");
        }
    };

    // Build filter options for react-select
    const locationOptions = filterOptions.locations.map(l => ({ value: l.id, label: l.complete_name }));
    const warehouseOptions = filterOptions.warehouses.map(w => ({ value: w.id, label: w.name }));
    const categoryOptions = filterOptions.categories.map(c => ({ value: c, label: c }));
    const productOptions = filterOptions.products.map(p => ({ value: p.id, label: p.display_name }));

    const SortIcon = ({ column }) => (
        <span className={`sort-icon ${sortKey === column ? 'active' : ''}`}>
            {sortKey === column ? (sortDir === 'asc' ? ' \u2191' : ' \u2193') : ' \u2195'}
        </span>
    );

    const formatValue = (val) => {
        if (val == null || val === 0) return '-';
        return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val);
    };

    const formatQuantity = (val) => {
        if (val == null) return '-';
        return new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2 }).format(val);
    };

    return (
        <div className="gestione-magazzino-modern">
            {/* Header */}
            <div className="page-header">
                <div className="header-content">
                    {!isWarehouseMode && (
                        <button className="back-button" onClick={() => navigate("/produttivita")}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M19 12H5M12 19l-7-7 7-7"/>
                            </svg>
                            Indietro
                        </button>
                    )}

                    <div className="header-title">
                        <div className="header-title-row">
                            <h2>Gestione Magazzino</h2>
                            <button
                                className="info-button-modern"
                                onClick={() => setShowInfo(true)}
                                title="Informazioni"
                            >
                                <span className="info-icon-text">i</span>
                            </button>
                        </div>
                        <p className="header-subtitle">
                            Monitoraggio giacenze e gestione inventario da Odoo
                        </p>
                    </div>
                </div>
            </div>

            {/* Info Modal */}
            {showInfo && (
                <div className="modal-overlay" onClick={() => setShowInfo(false)}>
                    <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="16" x2="12" y2="12"></line>
                                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                </svg>
                            </div>
                            <h3>Informazioni Gestione Magazzino</h3>
                            <button className="modal-close" onClick={() => setShowInfo(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="modal-section">
                                <h4>Cosa fa questa pagina?</h4>
                                <p>
                                    Questa sezione consente di visualizzare e gestire le <strong>giacenze di magazzino</strong> direttamente
                                    dal gestionale <strong>Odoo</strong>. I dati vengono recuperati in tempo reale tramite integrazione API
                                    e mostrano le quantita disponibili per ogni prodotto, suddivise per <strong>magazzino</strong> e <strong>ubicazione</strong>.
                                </p>
                            </div>

                            <div className="modal-section">
                                <h4>Informazioni visualizzate</h4>
                                <ul style={{ marginLeft: '20px', marginTop: '10px', lineHeight: '1.8' }}>
                                    <li><strong>Prodotto:</strong> Nome del prodotto presente in magazzino</li>
                                    <li><strong>Categoria:</strong> Categoria merceologica del prodotto</li>
                                    <li><strong>Ubicazione:</strong> Posizione specifica all'interno del magazzino</li>
                                    <li><strong>Quantita e UdM:</strong> Quantita disponibile con unita di misura primaria e secondarie</li>
                                </ul>
                            </div>

                            <div className="modal-section">
                                <h4>Modifica quantita</h4>
                                <p>
                                    Cliccando sul pulsante <strong>Modifica</strong> accanto a ogni riga, e possibile aggiornare
                                    la quantita di inventario. La modifica verra applicata direttamente su Odoo dopo conferma.
                                </p>
                            </div>

                            <div className="modal-section">
                                <h4>Filtri</h4>
                                <p>
                                    Utilizza i filtri per cercare prodotti per <strong>nome</strong>, filtrare per
                                    <strong> ubicazione</strong>, <strong>categoria</strong>, <strong>magazzino</strong> o <strong>prodotto</strong>. Clicca su "Applica" per aggiornare i risultati.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Content */}
            <div className="content-area">
                {loading && data.length === 0 ? (
                    <Lf23Loader />
                ) : (
                    <>
                        {/* Controls */}
                        <div className="controls-compact">
                            <div className="controls-left">
                                <button
                                    className={`toggle-filters-btn ${showFilters ? 'active' : ''}`}
                                    onClick={() => setShowFilters(!showFilters)}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                                    </svg>
                                    {showFilters ? 'Nascondi Filtri' : 'Mostra Filtri'}
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="6 9 12 15 18 9"></polyline>
                                    </svg>
                                </button>
                                <div className="results-badge">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                                    </svg>
                                    {total} prodotti
                                </div>
                            </div>
                            <div className="controls-right">
                                <div className="search-box">
                                    <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="11" cy="11" r="8"></circle>
                                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                    </svg>
                                    <input
                                        className="search-input"
                                        type="text"
                                        placeholder="Cerca prodotto..."
                                        value={productSearch}
                                        onChange={(e) => setProductSearch(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilters(); }}
                                    />
                                </div>
                                <button
                                    className="add-row-btn"
                                    onClick={() => setAddModalOpen(true)}
                                    title="Aggiungi prodotto"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <line x1="12" y1="5" x2="12" y2="19"></line>
                                        <line x1="5" y1="12" x2="19" y2="12"></line>
                                    </svg>
                                    Aggiungi
                                </button>
                            </div>
                        </div>

                        {/* Filter Panel */}
                        {showFilters && (
                            <div className="filter-panel">
                                <div className="filter-grid">
                                    <div className="filter-group">
                                        <label>Ubicazione</label>
                                        <Select
                                            isMulti
                                            classNamePrefix="filter-select"
                                            options={locationOptions}
                                            value={selectedLocations}
                                            onChange={(val) => setSelectedLocations(val || [])}
                                            placeholder="Tutte le ubicazioni"
                                            closeMenuOnSelect={false}
                                            noOptionsMessage={() => "Nessuna opzione"}
                                        />
                                    </div>
                                    <div className="filter-group">
                                        <label>Categoria</label>
                                        <Select
                                            isMulti
                                            classNamePrefix="filter-select"
                                            options={categoryOptions}
                                            value={selectedCategories}
                                            onChange={(val) => setSelectedCategories(val || [])}
                                            placeholder="Tutte le categorie"
                                            closeMenuOnSelect={false}
                                            noOptionsMessage={() => "Nessuna opzione"}
                                        />
                                    </div>
                                    <div className="filter-group">
                                        <label>Magazzino</label>
                                        <Select
                                            isMulti
                                            classNamePrefix="filter-select"
                                            options={warehouseOptions}
                                            value={selectedWarehouses}
                                            onChange={(val) => setSelectedWarehouses(val || [])}
                                            placeholder="Tutti i magazzini"
                                            closeMenuOnSelect={false}
                                            noOptionsMessage={() => "Nessuna opzione"}
                                        />
                                    </div>
                                    <div className="filter-group">
                                        <label>Prodotto</label>
                                        <Select
                                            isMulti
                                            classNamePrefix="filter-select"
                                            options={productOptions}
                                            value={selectedProducts}
                                            onChange={(val) => setSelectedProducts(val || [])}
                                            placeholder="Tutti i prodotti"
                                            closeMenuOnSelect={false}
                                            noOptionsMessage={() => "Nessuna opzione"}
                                        />
                                    </div>
                                </div>
                                <div className="filter-actions">
                                    <button className="filter-reset-btn" onClick={handleResetFilters}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="1 4 1 10 7 10"></polyline>
                                            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
                                        </svg>
                                        Reset
                                    </button>
                                    <button className="filter-apply-btn" onClick={handleApplyFilters}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="20 6 9 17 4 12"></polyline>
                                        </svg>
                                        Applica
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Stats */}
                        <div className="stats-grid-compact">
                            <div className="stat-card-compact stat-primary">
                                <div className="stat-icon-compact">
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                                    </svg>
                                </div>
                                <div className="stat-content-compact">
                                    <p className="stat-label-compact">Totale Prodotti</p>
                                    <p className="stat-value-compact">{formatQuantity(stats.totalProducts)}</p>
                                </div>
                            </div>
                            <div className="stat-card-compact stat-warning">
                                <div className="stat-icon-compact">
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="1" y="3" width="15" height="13"></rect>
                                        <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                                        <circle cx="5.5" cy="18.5" r="2.5"></circle>
                                        <circle cx="18.5" cy="18.5" r="2.5"></circle>
                                    </svg>
                                </div>
                                <div className="stat-content-compact">
                                    <p className="stat-label-compact">Magazzini</p>
                                    <p className="stat-value-compact">{stats.totalWarehouses}</p>
                                </div>
                            </div>
                            <div className="stat-card-compact stat-info">
                                <div className="stat-icon-compact">
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                        <circle cx="12" cy="10" r="3"></circle>
                                    </svg>
                                </div>
                                <div className="stat-content-compact">
                                    <p className="stat-label-compact">Ubicazioni</p>
                                    <p className="stat-value-compact">{stats.totalLocations}</p>
                                </div>
                            </div>
                        </div>

                        {/* Table */}
                        {sortedData.length > 0 ? (
                            <>
                                {/* Desktop Table */}
                                <div className="table-container-compact desktop-only">
                                    <div className="table-wrapper">
                                        <table className="data-table-compact">
                                            <thead>
                                                <tr>
                                                    <th onClick={() => handleSort('product_name')}>
                                                        <div className="th-content">Prodotto <SortIcon column="product_name" /></div>
                                                    </th>
                                                    <th onClick={() => handleSort('product_category')}>
                                                        <div className="th-content">Categoria <SortIcon column="product_category" /></div>
                                                    </th>
                                                    <th onClick={() => handleSort('location_name')}>
                                                        <div className="th-content">Ubicazione <SortIcon column="location_name" /></div>
                                                    </th>
                                                    <th onClick={() => handleSort('quantity')}>
                                                        <div className="th-content">Quantita <SortIcon column="quantity" /></div>
                                                    </th>
                                                    <th>UdM</th>
                                                    <th>UdM Secondarie</th>
                                                    <th style={{textAlign:'center'}}>Azioni</th>
                                                    <th>Gestito</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedData.map((row, idx) => (
                                                    <tr key={`${row.id}-${idx}`} className={row.gestito ? 'row-gestito' : ''}>
                                                        <td className="product-name-cell">{row.product_name || '-'}</td>
                                                        <td>
                                                            {row.product_category ? (
                                                                <span className="category-badge">{row.product_category}</span>
                                                            ) : '-'}
                                                        </td>
                                                        <td>{row.location_name || '-'}</td>
                                                        <td className="quantity-cell">
                                                            {row.gestito && row.old_quantity != null ? (
                                                                <span className="quantity-with-old">
                                                                    <span className="old-quantity">{formatQuantity(row.old_quantity)}</span>
                                                                    <span className="quantity-arrow">&rarr;</span>
                                                                    {formatQuantity(row.quantity)}
                                                                </span>
                                                            ) : formatQuantity(row.quantity)}
                                                        </td>
                                                        <td>{row.uom || '-'}</td>
                                                        <td>
                                                            {row.secondary_uoms && row.secondary_uoms.length > 0 ? (
                                                                <div className="secondary-uom">
                                                                    {row.secondary_uoms.map((su, i) => (
                                                                        <span key={i}>
                                                                            {formatQuantity(su.quantity)} {su.uom_name}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            ) : '-'}
                                                        </td>
                                                        <td>
                                                            <div className="action-buttons">
                                                                <button
                                                                    className="edit-btn-compact"
                                                                    onClick={() => handleEditClick(row)}
                                                                    title="Modifica quantita"
                                                                >
                                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                                    </svg>
                                                                </button>
                                                                <button
                                                                    className="confirm-btn-compact"
                                                                    onClick={() => handleConfirmQuantity(row)}
                                                                    title="Conferma quantita"
                                                                    disabled={row.gestito}
                                                                >
                                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                                        <path d="M20 6L9 17l-5-5"/>
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {row.gestito && (
                                                                <span className="gestito-icon" title="Gestito oggi">
                                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5">
                                                                        <path d="M20 6L9 17l-5-5"/>
                                                                    </svg>
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Mobile Card View */}
                                <div className="mobile-cards-container mobile-only">
                                    {sortedData.map((row, idx) => (
                                        <div key={`card-${row.id}-${idx}`} className={`mobile-card ${row.gestito ? 'mobile-card-gestito' : ''}`}>
                                            <div className="mobile-card-header">
                                                <div className="mobile-card-title">{row.product_name || '-'}</div>
                                                {row.gestito && (
                                                    <span className="gestito-icon" title="Gestito oggi">
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5">
                                                            <path d="M20 6L9 17l-5-5"/>
                                                        </svg>
                                                    </span>
                                                )}
                                            </div>
                                            {row.product_category && (
                                                <span className="category-badge">{row.product_category}</span>
                                            )}
                                            <div className="mobile-card-body">
                                                <div className="mobile-card-row">
                                                    <span className="mobile-card-label">Ubicazione</span>
                                                    <span className="mobile-card-value">{row.location_name || '-'}</span>
                                                </div>
                                                <div className="mobile-card-row">
                                                    <span className="mobile-card-label">Quantita</span>
                                                    <span className="mobile-card-value quantity-cell">
                                                        {row.gestito && row.old_quantity != null ? (
                                                            <span className="quantity-with-old">
                                                                <span className="old-quantity">{formatQuantity(row.old_quantity)}</span>
                                                                <span className="quantity-arrow">&rarr;</span>
                                                                {formatQuantity(row.quantity)}
                                                            </span>
                                                        ) : formatQuantity(row.quantity)}
                                                        {' '}{row.uom || ''}
                                                    </span>
                                                </div>
                                                {row.secondary_uoms && row.secondary_uoms.length > 0 && (
                                                    <div className="mobile-card-row">
                                                        <span className="mobile-card-label">UdM Sec.</span>
                                                        <span className="mobile-card-value">
                                                            {row.secondary_uoms.map((su, i) => (
                                                                <span key={i} className="mobile-secondary-uom">
                                                                    {formatQuantity(su.quantity)} {su.uom_name}
                                                                </span>
                                                            ))}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="mobile-card-actions">
                                                <button
                                                    className="mobile-card-edit-btn"
                                                    onClick={() => handleEditClick(row)}
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                    </svg>
                                                    Modifica
                                                </button>
                                                <button
                                                    className="mobile-card-confirm-btn"
                                                    onClick={() => handleConfirmQuantity(row)}
                                                    disabled={row.gestito}
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                        <path d="M20 6L9 17l-5-5"/>
                                                    </svg>
                                                    Conferma
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Pagination */}
                                <div className="pagination-compact">
                                    <button
                                        className="pagination-btn-compact"
                                        disabled={page === 1}
                                        onClick={() => setPage(page - 1)}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M15 18l-6-6 6-6"/>
                                        </svg>
                                        Prec
                                    </button>
                                    <div className="pagination-info-compact">
                                        <span className="pagination-text-compact">
                                            Pag <strong>{page}</strong> di <strong>{totalPages || 1}</strong>
                                        </span>
                                        <span className="pagination-total-compact">({total} risultati totali)</span>
                                    </div>
                                    <button
                                        className="pagination-btn-compact"
                                        disabled={page === totalPages || totalPages === 0}
                                        onClick={() => setPage(page + 1)}
                                    >
                                        Succ
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M9 18l6-6-6-6"/>
                                        </svg>
                                    </button>

                                    <div className="items-per-page-compact">
                                        <span className="items-label-compact">Elementi per pagina:</span>
                                        <div className="items-buttons-compact">
                                            {[10, 20, 50, 100].map((num) => (
                                                <button
                                                    key={num}
                                                    className={`items-btn-compact ${limit === num ? "active" : ""}`}
                                                    onClick={() => {
                                                        setLimit(num);
                                                        setPage(1);
                                                    }}
                                                >
                                                    {num}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            !loading && (
                                <div className="empty-state">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                                    </svg>
                                    <p>Nessun prodotto trovato</p>
                                </div>
                            )
                        )}

                        {loading && data.length > 0 && (
                            <Lf23Loader />
                        )}
                    </>
                )}
            </div>

            {/* Edit Modal */}
            {editModalOpen && editingItem && (
                <div className="modal-overlay" onClick={handleCancelEdit}>
                    <div className="edit-modal-container" onClick={(e) => e.stopPropagation()}>
                        <div className="edit-modal-header">
                            <div className="modal-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                </svg>
                            </div>
                            <h3>Modifica Quantita</h3>
                            <button className="modal-close" onClick={handleCancelEdit}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="edit-modal-body">
                            <div className="edit-modal-info">
                                <div className="edit-modal-info-row">
                                    <span className="edit-modal-info-label">Prodotto</span>
                                    <span className="edit-modal-info-value">{editingItem.product_name}</span>
                                </div>
                                <div className="edit-modal-info-row">
                                    <span className="edit-modal-info-label">Ubicazione</span>
                                    <span className="edit-modal-info-value">{editingItem.location_name}</span>
                                </div>
                                <div className="edit-modal-info-row">
                                    <span className="edit-modal-info-label">Quantita Attuale</span>
                                    <span className="edit-modal-info-value">
                                        {formatQuantity(editingItem.quantity)} {editingItem.uom}
                                    </span>
                                </div>
                                {editingItem.secondary_uoms && editingItem.secondary_uoms.length > 0 && editingItem.secondary_uoms.map((su, idx) => (
                                    <div className="edit-modal-info-row" key={idx}>
                                        <span className="edit-modal-info-label">Quantita Attuale ({su.uom_name})</span>
                                        <span className="edit-modal-info-value">
                                            {formatQuantity(su.quantity)} {su.uom_name}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className="edit-modal-field">
                                <label>Nuova Quantita ({editingItem.uom})</label>
                                <input
                                    className="edit-modal-input"
                                    type="number"
                                    step="any"
                                    value={newQuantity}
                                    onChange={(e) => setNewQuantity(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && !saving) handleConfirmEdit(); }}
                                    autoFocus
                                />
                            </div>
                            {editingItem.secondary_uoms && editingItem.secondary_uoms.length > 0 && editingItem.secondary_uoms.map((su, idx) => {
                                const newSecQty = newQuantity !== '' && su.factor ? parseFloat(newQuantity) / su.factor : '';
                                return (
                                    <div className="edit-modal-field" key={idx}>
                                        <label>Nuova Quantita ({su.uom_name})</label>
                                        <input
                                            className="edit-modal-input"
                                            type="number"
                                            step="any"
                                            value={newSecQty !== '' ? newSecQty : ''}
                                            onChange={(e) => {
                                                const secVal = e.target.value;
                                                if (secVal === '') {
                                                    setNewQuantity('');
                                                } else {
                                                    setNewQuantity(String(parseFloat(secVal) * su.factor));
                                                }
                                            }}
                                            onKeyDown={(e) => { if (e.key === 'Enter' && !saving) handleConfirmEdit(); }}
                                        />
                                    </div>
                                );
                            })}

                            <div className="edit-modal-actions">
                                <button className="btn-cancel" onClick={handleCancelEdit}>
                                    Annulla
                                </button>
                                <button
                                    className="btn-confirm"
                                    onClick={handleConfirmEdit}
                                    disabled={saving || newQuantity === "" || parseFloat(newQuantity) === editingItem.quantity}
                                >
                                    {saving ? (
                                        <>
                                            <div className="btn-spinner"></div>
                                            Salvataggio...
                                        </>
                                    ) : (
                                        'Conferma Modifica'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Modal */}
            {addModalOpen && (
                <div className="modal-overlay" onClick={handleCancelAdd}>
                    <div className="edit-modal-container" onClick={(e) => e.stopPropagation()}>
                        <div className="edit-modal-header" style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}>
                            <div className="modal-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                            </div>
                            <h3>Aggiungi Prodotto</h3>
                            <button className="modal-close" onClick={handleCancelAdd}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="edit-modal-body">
                            <div className="add-modal-field">
                                <label>Prodotto</label>
                                <Select
                                    classNamePrefix="filter-select"
                                    options={productOptions}
                                    value={addProduct}
                                    onChange={setAddProduct}
                                    placeholder="Seleziona prodotto..."
                                    noOptionsMessage={() => "Nessun prodotto trovato"}
                                    isClearable
                                />
                            </div>
                            <div className="add-modal-field">
                                <label>Ubicazione</label>
                                <Select
                                    classNamePrefix="filter-select"
                                    options={locationOptions}
                                    value={addLocation}
                                    onChange={setAddLocation}
                                    placeholder="Seleziona ubicazione..."
                                    noOptionsMessage={() => "Nessuna ubicazione trovata"}
                                    isClearable
                                />
                            </div>
                            <div className="add-modal-field">
                                <label>Quantita</label>
                                <input
                                    className="edit-modal-input"
                                    type="number"
                                    step="any"
                                    value={addQuantity}
                                    onChange={(e) => setAddQuantity(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && !addSaving) handleConfirmAdd(); }}
                                    placeholder="0"
                                />
                            </div>

                            <div className="edit-modal-actions">
                                <button className="btn-cancel" onClick={handleCancelAdd}>
                                    Annulla
                                </button>
                                <button
                                    className="btn-confirm"
                                    style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}
                                    onClick={handleConfirmAdd}
                                    disabled={addSaving || !addProduct || !addLocation || addQuantity === ""}
                                >
                                    {addSaving ? (
                                        <>
                                            <div className="btn-spinner"></div>
                                            Inserimento...
                                        </>
                                    ) : (
                                        'Inserisci'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast */}
            {toast && (
                <div className={`toast-message toast-${toast.type}`}>
                    {toast.message}
                </div>
            )}
        </div>
    );
};

export default GestioneMagazzino;
